import { fromUnixTime } from 'date-fns';
import { AuctionTypeEnum } from '@soundxyz/utils/const';
import type { GetDraftQuery } from '@graphql/reactQuery';
import { SAM_DEFAULT_ENABLED } from '@utils/constants';
import type { DraftType } from '../helpers';
import { validateEditedAuctionConfigState } from './edit/validation';
import type { AuctionConfigurationErrorKeys } from './errors';
import { initialAuctionConfigurationErrors } from './errors';
import type {
  AuctionConfigurationFieldOptionsType,
  CollectorsOfArtistsKeys,
  CollectorsOfReleasesKeys,
} from './fields';
import { auctionConfigurationState, isAuctionConfigUnnestedFieldKeysType } from './fields';
import { validateAuctionConfiguration } from './validation';

export type AuctionConfigurationDataInfo = NonNullable<
  NonNullable<GetDraftQuery['draft']['info']>['auctionConfigurations']
>;

type DraftAllowlistInfo = NonNullable<NonNullable<GetDraftQuery['draft']['info']>['allowListInfo']>;

export const clearAuctionConfigurationErrors = () => {
  auctionConfigurationState.errors = initialAuctionConfigurationErrors();
};

export const mapAuctionConfigurationToFormValues = (
  auctionConfigData: AuctionConfigurationDataInfo,
  allowlistData: DraftAllowlistInfo | null,
  type: DraftType,
) => {
  let presaleStartTime: Date | null = null;

  if (auctionConfigData.presaleMint) {
    presaleStartTime = fromUnixTime(auctionConfigData.presaleMint.startTime);
    auctionConfigurationState.hasPresaleMint = true;
  }

  const publicStartTime = fromUnixTime(auctionConfigData.publicMint.startTime);

  const selectedReleases = allowlistData?.presaleMint?.collectorsOfReleases?.selectedReleases.map(
    release => {
      return release.id;
    },
  );

  const selectedArtists = allowlistData?.presaleMint?.collectorsOfArtists?.selectedArtists.map(
    artist => {
      return artist.id;
    },
  );

  auctionConfigurationState.fields = {
    auctionType: auctionConfigData.auctionType,
    enabledSAM: SAM_DEFAULT_ENABLED,
    presaleEndTime: auctionConfigData.presaleMint?.endTime
      ? fromUnixTime(auctionConfigData.presaleMint.endTime)
      : null,
    publicEndTime: auctionConfigData.publicMint?.endTime
      ? fromUnixTime(auctionConfigData.publicMint?.endTime)
      : null,
    maxMintable: auctionConfigData.maxMintable,
    minQuantity:
      auctionConfigData.auctionType === AuctionTypeEnum.RANGE_BOUND ||
      auctionConfigData.auctionType === AuctionTypeEnum.OPEN_EDITION
        ? auctionConfigData.minQuantity
        : '',
    maxQuantity:
      auctionConfigData.auctionType === AuctionTypeEnum.RANGE_BOUND ||
      auctionConfigData.auctionType === AuctionTypeEnum.OPEN_EDITION
        ? auctionConfigData.maxMintable
        : '',
    publicStartTime,
    presaleStartTime,
    presalePricePerMint: auctionConfigData.presaleMint?.price,
    presaleMaxSupply: auctionConfigData.presaleMint?.quantity,
    presaleMaxMintsPerWallet: auctionConfigData?.presaleMint?.maxMintsPerWallet,
    publicMaxMintsPerWallet: auctionConfigData?.publicMint?.maxMintsPerWallet,
    publicPricePerMint: auctionConfigData.publicMint.price,
    quantityBreakdown: auctionConfigData.quantityBreakdown ?? [],
    collectorsOfReleases: {
      selectedReleaseIds: selectedReleases ?? [],
      allArtistCollaborations:
        allowlistData?.presaleMint?.collectorsOfReleases?.allArtistCollaborations ?? false,
      allArtistReleases:
        allowlistData?.presaleMint?.collectorsOfReleases?.allArtistReleases ?? false,
    },
    collectorsOfArtists: {
      allArtistCollectors:
        allowlistData?.presaleMint?.collectorsOfArtists?.allArtistCollectors ?? true,
      selectedArtistIds: selectedArtists ?? [],
      allCollaboratingArtists:
        allowlistData?.presaleMint?.collectorsOfArtists?.allCollaboratingArtists ?? false,
    },
    manuallyAddedAllowlist: {
      description: allowlistData?.presaleMint?.manuallyAddedAllowlist?.description,
      hasAddresses: null,
    },
  };

  auctionConfigurationState.touched = {
    maxMintable: true,
    maxQuantity: true,
    minQuantity: true,
    presaleMaxMintsPerWallet: true,
    presaleMaxSupply: true,
    presalePricePerMint: true,
    presaleStartTime: true,
    presaleEndTime: true,
    publicEndTime: true,
    publicMaxMintsPerWallet: true,
    publicPricePerMint: true,
    publicStartTime: true,
    quantityBreakdown: true,
    collectorsOfReleases: true,
    collectorsOfArtists: true,
    manuallyAddedAllowlist: {
      description: true,
      hasAddresses: true,
    },
  };

  validateAuctionConfiguration();

  type === 'edit' && validateEditedAuctionConfigState();
};

export const setAuctionConfigurationField = (
  name: AuctionConfigurationFieldOptionsType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  touched?: boolean,
) => {
  if (!isAuctionConfigUnnestedFieldKeysType(name)) return;
  auctionConfigurationState.fields[name] = value;
  auctionConfigurationState.touched[name] = touched || false;
};

export const setAuctionConfigurationError = (
  name: AuctionConfigurationErrorKeys,
  value: string | null,
) => {
  if (!isAuctionConfigUnnestedFieldKeysType(name)) return;
  auctionConfigurationState.errors[name] = value;
};

export const setAuctionType = (value: AuctionTypeEnum) => {
  auctionConfigurationState.fields.auctionType = value;
};

export const setAllAuctionConfigurationFieldsTouched = () => {
  auctionConfigurationState.touched = {
    maxMintable: true,
    maxQuantity: true,
    minQuantity: true,
    presaleMaxMintsPerWallet: true,
    presaleMaxSupply: true,
    presalePricePerMint: true,
    presaleStartTime: true,
    presaleEndTime: true,
    publicEndTime: true,
    publicMaxMintsPerWallet: true,
    publicPricePerMint: true,
    publicStartTime: true,
    quantityBreakdown: true,
    collectorsOfReleases: true,
    collectorsOfArtists: true,
    manuallyAddedAllowlist: {
      description: true,
      hasAddresses: true,
    },
  };
};

export const setManuallyAddedAllowlistFields = ({
  name,
  value,
  touched,
}: {
  name: 'description' | 'hasAddresses';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  touched?: boolean;
}) => {
  if (!auctionConfigurationState.fields.manuallyAddedAllowlist) {
    auctionConfigurationState.fields.manuallyAddedAllowlist = {
      description: null,
      hasAddresses: null,
    };
  }

  auctionConfigurationState.fields.manuallyAddedAllowlist[name] = value;
  auctionConfigurationState.touched.manuallyAddedAllowlist[name] = touched || false;
};

export const setCollectorsOfReleasesFields = ({
  name,
  value,
  touched,
}: {
  name: CollectorsOfReleasesKeys;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  touched?: boolean;
}) => {
  if (!auctionConfigurationState.fields.collectorsOfReleases) {
    auctionConfigurationState.fields.collectorsOfReleases = {
      selectedReleaseIds: [],
      allArtistCollaborations: false,
      allArtistReleases: false,
    };
  }

  auctionConfigurationState.fields.collectorsOfReleases[name] = value;
  auctionConfigurationState.touched.collectorsOfReleases = touched || false;
};

export const setCollectorsOfArtistsFields = ({
  name,
  value,
  touched,
}: {
  name: CollectorsOfArtistsKeys;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  touched?: boolean;
}) => {
  if (!auctionConfigurationState.fields.collectorsOfArtists) {
    auctionConfigurationState.fields.collectorsOfArtists = {
      allArtistCollectors: true,
      selectedArtistIds: [],
      allCollaboratingArtists: false,
    };
  }

  auctionConfigurationState.fields.collectorsOfArtists[name] = value;
  auctionConfigurationState.touched.collectorsOfArtists = touched || false;
};
